import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../firebase";
import { api } from "../api";
import { Logo } from "../components/Logo";

export function SignInPage() {
  const location = useLocation();
  // Invite tokens arrive via ?invite=<token> on the sign-in route. We read
  // them once on mount; redemption happens after the auth step succeeds so
  // we have a real uid + email to attach to the workspace.
  const inviteToken = useMemo(() => {
    const params = new URLSearchParams(location.search || (location.hash.split("?")[1] ?? ""));
    return params.get("invite");
  }, [location.search, location.hash]);

  const [mode, setMode] = useState<"signin" | "signup" | "reset">(inviteToken ? "signup" : "signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Helpful hint when the user lands via an invite link.
  useEffect(() => {
    if (inviteToken) {
      setNote("You've been invited to join a Ventrin workspace. Create an account or sign in below — we'll attach you automatically.");
    }
  }, [inviteToken]);

  async function resetPassword() {
    setErr(null);
    setNote(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Enter your email first, then click Reset password.");
      return;
    }
    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setNote("If that email has an account, a reset link is on its way.");
    } catch (ex: any) {
      // Don't leak which emails exist — treat both success and failure the same.
      setNote("If that email has an account, a reset link is on its way.");
    } finally {
      setBusy(false);
    }
  }

  async function signInAsDemo() {
    setErr(null);
    setBusy(true);
    try {
      const creds = await api.getDemoCredentials();
      await signInWithCustomToken(auth, creds.customToken);
      navigate("/");
    } catch (ex: any) {
      setErr(ex?.message ?? "Could not sign in as demo.");
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setNote(null);
    if (mode === "reset") {
      return resetPassword();
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr("Enter a valid work email");
      return;
    }
    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        // Only send a verification email when we're NOT redeeming an
        // invite. Invitees come pre-vetted (the admin trusted them with
        // the link) and we attach them to the workspace immediately,
        // which the email-verification gate doesn't apply to.
        if (!inviteToken) {
          sendEmailVerification(cred.user).catch(() => undefined);
        }
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      if (inviteToken) {
        try {
          await api.redeemInvite({ token: inviteToken });
        } catch (e: any) {
          setErr(humanise(e?.message ?? "Could not redeem invite."));
          setBusy(false);
          return;
        }
      }
      navigate("/");
    } catch (ex: any) {
      setErr(humanise(ex?.message ?? "Authentication failed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sg-auth-shell">
      <aside className="sg-auth-left">
        <div className="sg-auth-left-top">
          <span style={{ color: "#e6e9ef" }}><Logo size={22} /></span>
          <div className="sg-brand-name">Ventrin</div>
        </div>
        <div className="sg-auth-left-mid">
          <h1 className="sg-auth-headline">Prompt firewall for regulated teams</h1>
          <ul className="sg-auth-promise" style={{ padding: 0, margin: 0 }}>
            <li>Prevent sensitive data leaving your organisation</li>
            <li>Enforce AI usage policies in real time</li>
            <li>Works across ChatGPT, Claude, and Gemini</li>
          </ul>
        </div>
      </aside>

      <main className="sg-auth-right">
        <div className="sg-auth-form-wrap">
          <h2 className="sg-auth-title">
            {mode === "signup" ? "Set up your workspace" : mode === "reset" ? "Reset your password" : "Sign in"}
          </h2>
          <div className="sg-auth-sub">
            {mode === "signup"
              ? "Control how your team uses AI tools."
              : mode === "reset"
              ? "Enter your email. We'll send a reset link."
              : "Access your workspace and review events."}
          </div>

          <form onSubmit={submit} noValidate>
            <div className="sg-field">
              <label className="sg-label">Work Email</label>
              <input
                className="sg-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {mode !== "reset" && (
              <div className="sg-field">
                <label className="sg-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span>Password</span>
                  {mode === "signin" && (
                    <a
                      href="#"
                      style={{ fontSize: 11.5, textDecoration: "none" }}
                      onClick={(e) => { e.preventDefault(); setMode("reset"); setErr(null); setNote(null); }}
                    >
                      Forgot password?
                    </a>
                  )}
                </label>
                <input
                  className="sg-input"
                  type="password"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
              </div>
            )}
            {err && <div style={{ color: "var(--sg-red)", fontSize: 12, marginBottom: 10 }}>{err}</div>}
            {note && <div style={{ color: "var(--sg-ink-3)", fontSize: 12, marginBottom: 10 }}>{note}</div>}
            <button className="sg-btn sg-btn-primary" type="submit" disabled={busy} style={{ width: "100%" }}>
              {busy ? "Working…" : mode === "signup" ? "Create Workspace" : mode === "reset" ? "Send Reset Link" : "Sign In"}
            </button>
          </form>

          {mode !== "reset" && (
            <>
              <div className="sg-auth-divider">Or</div>
              <button type="button" className="sg-btn" disabled={busy} onClick={signInAsDemo} style={{ width: "100%" }}>
                {busy ? "Loading Demo…" : "Try Demo Workspace"}
              </button>
            </>
          )}

          <div className="sg-auth-footer">
            {mode === "signup" && (
              <>Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); setMode("signin"); setErr(null); setNote(null); }}>Sign in</a></>
            )}
            {mode === "signin" && (
              <>New here? <a href="#" onClick={(e) => { e.preventDefault(); setMode("signup"); setErr(null); setNote(null); }}>Create an account</a></>
            )}
            {mode === "reset" && (
              <>Remembered it? <a href="#" onClick={(e) => { e.preventDefault(); setMode("signin"); setErr(null); setNote(null); }}>Back to sign in</a></>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function humanise(msg: string): string {
  if (/email-already-in-use/i.test(msg)) return "An account with that email already exists.";
  if (/wrong-password|user-not-found|invalid-credential/i.test(msg)) return "Email or password is incorrect.";
  if (/weak-password/i.test(msg)) return "Password is too weak.";
  return msg.replace(/^Firebase:\s*/i, "").replace(/\s*\(auth[^)]+\)\s*\.?$/i, "");
}
